import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Button, Card, Divider, Icon, LazyImage, useDevice } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import useCarousel from 'hooks/useCarousel';
import { useSwiperController } from 'hooks/useSwiperController';
import { useQuoteDetailsModal } from 'hooks/useQuoteDetailsModal';

import { getApartment, getUserRequests } from 'store/sagas/configurations/configurations.saga';
import { applicationStateSelectors } from 'store/slices/applicationState/selectors';
import { getClientIdSelector } from 'store/slices/auth/selectors';
import { environmentSelectors } from 'store/slices/environment/selector';

import paths from 'routes/routesPaths';
import { Layout } from 'pages/Layout';

import { CardApartmentCarousel } from 'components/CardApartment/CardApartmentCarousel';
import { CardApartmentInfo } from 'components/CardApartment/CardApartmentInfo';
import { CardQuoteContainer } from 'components/CardQuoteContainer';
import { FooterSection } from 'components/FooterSection/FooterSection';
import { ContactUsModal } from 'components/ContactUsModal';
import { HighlightedContent } from 'components/HighlightedContent';
import { ModalImageContainer } from 'components/ModalImageContainer';
import { PageLoader } from 'components/PageLoader';
import { QuoteDetailsModal } from 'components/QuoteDetailsModal';
import { SendRequestModal } from 'components/SendRequestModal/SendRequestModal';
import type { CardApartmentProps } from 'components/CardApartment/CardApartment';

import { addConfigurationRent } from 'utils/addConfigurationRent';
import { getBannerProps, getRoomBannerProps } from 'utils/getBannerProps';
import { getPlanImage } from 'utils/getPlanImage';
import { mapApartmentRequestToQuote, mapRoomRequestToQuote } from 'utils/userRequestMappers';
import { formatDate } from 'utils/formatDates';
import { truncateTo1Decimal } from 'utils/formatNumbers';
import { sortQuotesByStatus } from 'utils/status';
import { redirectToConfigurator } from 'utils/redirectToConfigurator';

import { useFeaturesDownload } from 'hooks/useFeatiresDownload';
import { useProjectAssetType } from 'hooks/useProjectAssetType';

import { SELECTED_QUOTE_STATUSES } from 'constants/quoteStatus';

import type { AssetType } from 'types/project';
import type { Quote, QuoteStatus } from 'types/quote';

import './details-page.scss';

const DetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const device = useDevice();
  const projectInfo = useSelector(applicationStateSelectors.selectProjectInfo);
  const spaceConfigurator = useSelector(applicationStateSelectors.selectSpaceConfigurator);
  const { assetTypology, area } = useProjectAssetType();
  const coliving = useSelector(applicationStateSelectors.selectColiving);
  const environment = useSelector(environmentSelectors.environment);
  const clientId = useSelector(getClientIdSelector);
  const statuses = useSelector(applicationStateSelectors.selectStatuses);
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [apartment, setApartment] = useState<CardApartmentProps>();
  const [modalImages, setModalImages] = useState<string | string[]>('');
  const [openImageContainerModal, setOpenImageContainerModal] = useState(false);
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const [isSendRequestFlow, setIsSendRequestFlow] = useState(false);
  const [showDownloadFeaturesButton, setShowDownloadFeaturesButton] = useState(false);
  const { checkIfShouldShowDownloadFeaturesButton, onClickSeeAllFeatures } = useFeaturesDownload(apartment?.code ?? '');

  const planImage = {
    apartment: '',
    room: '',
  };

  const hasSelectedQuote = SELECTED_QUOTE_STATUSES.includes(apartment?.status as QuoteStatus);

  if (projectInfo && apartment) {
    const { area: projectArea, displayName } = projectInfo;
    const { planName, planNameRoom, building } = apartment;
    planImage.apartment = getPlanImage({ area: projectArea, displayName, planName, building });
    planImage.room = getPlanImage({ area: projectArea, displayName, planName: planNameRoom, building, assetTypology });
  }

  const carouselProps = useCarousel(apartment?.images.length ?? 0);

  const getQuotes = async (spaceId: string) => {
    const userRequests = await getUserRequests(projectInfo?.id ?? '', clientId ?? '', statuses!, spaceId);
    const quotes = userRequests.map((quoteRequest) =>
      assetTypology === 'room' ? mapRoomRequestToQuote(quoteRequest) : mapApartmentRequestToQuote(quoteRequest),
    );
    const sortedQuotes = sortQuotesByStatus(quotes);
    setQuotes(sortedQuotes);
  };

  const getApartmentDetails = async (spaceId: string, assetTypology: AssetType) => {
    const imagesBaseUrl = `${environment.REACT_APP_BUCKET_BASEURL}/initiatives/${encodeURI(projectInfo?.displayName || '')}`;
    const data = await getApartment(projectInfo?.id ?? '', spaceId, assetTypology, quotes, imagesBaseUrl);
    setApartment(data);
    setLoading(false);
  };
  const openPreviousQuotesModal = () => {
    openQuoteWithPreviousQuotes();
  };

  const toggleImageContainerModal = (e?: SyntheticEvent, images?: string | string[]) => {
    e?.stopPropagation();
    if (images) {
      setModalImages(images);
    }
    setOpenImageContainerModal(!openImageContainerModal);
  };
  const toggleContactUsModal = () => {
    setIsContactUsModalOpen(!isContactUsModalOpen);
  };

  const bannerProps =
    assetTypology === 'room'
      ? getRoomBannerProps({
          t,
          sendRequestAction: () => {
            setIsSendRequestFlow(true);
            toggleQuoteDetailsModal();
          },
          contactUsAction: toggleContactUsModal,
          createConfigurationAction: () => redirectToConfigurator({ assetType: assetTypology, spaceConfigurator, coliving }),
          checkInDate: quotes[0]?.dateRange?.checkInDate ? formatDate(new Date(quotes[0]?.dateRange?.checkInDate)) : '',
        })
      : getBannerProps({
          t,
          firstButtonAction: toggleContactUsModal,
          secondButtonAction: toggleContactUsModal,
          thirdButtonAction: spaceConfigurator?.enabled
            ? () => addConfigurationRent(spaceConfigurator, apartment?.building?.complex)
            : toggleContactUsModal,
          sendRequestAction: () => {
            setIsSendRequestFlow(true);
            toggleQuoteDetailsModal();
          },
          contactUsAction: toggleContactUsModal,
          checkInDate: quotes[0]?.dateRange?.checkInDate ? formatDate(new Date(quotes[0]?.dateRange?.checkInDate)) : '',
          area,
        });

  if (assetTypology !== 'room' && area && area !== 'rent' && 'interest' in bannerProps && bannerProps.interest) {
    const apartmentBannerProps = bannerProps as ReturnType<typeof getBannerProps>;
    apartmentBannerProps.interest = {
      ...apartmentBannerProps.interest,
      iconName: 'myhome-contact',
      subtitle: t('contact-us-modal.highlighted-content.subtitle'),
      firstButton: {
        label: t('card-apartment.highlighted-content.button-label.contact-us'),
        onClick: toggleContactUsModal,
        iconName: 'mail',
      },
      secondButton: projectInfo?.contactPhone
        ? {
            label: projectInfo?.contactPhone,
            onClick: () => {
              window.location.href = `tel:${projectInfo?.contactPhone?.replaceAll('.', '').replaceAll(' ', '')}`;
            },
            iconName: 'phone',
          }
        : undefined,
    };
  }

  const {
    isQuoteDetailsModalOpen,
    quoteList,
    handleOnClose,
    openQuoteWithPreviousQuotes,
    toggleQuoteDetailsModal,
    isSendRequestModalOpen,
    setIsSendRequestModalOpen,
    selectedQuote,
    getModalTitle,
    getModalDescription,
    handleSendRequest,
  } = useQuoteDetailsModal({
    quotes: quotes,
    apartmentName: `${t(`apartment-model.${apartment?.typologyName}`, { defaultValue: apartment?.typologyName })} ${apartment?.name}`,
    isSendRequestFlow,
    space: apartment
      ? {
          code: apartment.code,
          name: apartment.name,
          building: apartment.building,
        }
      : undefined,
  });

  const handleOnQuoteDeleted = () => {
    setLoading(true);
    if (quotes.length > 1) {
      getQuotes(params.apartmentId!);
    } else {
      navigate(paths.root);
    }
  };

  useEffect(() => {
    setLoading(true);
    getQuotes(params.apartmentId!);
  }, []);

  useEffect(() => {
    (async () => {
      const shouldShowDownloadFeaturesButton = await checkIfShouldShowDownloadFeaturesButton();
      setShowDownloadFeaturesButton(shouldShowDownloadFeaturesButton);
    })();
  }, [apartment?.code]);

  useEffect(() => {
    if (quotes.length) {
      getApartmentDetails(params.apartmentId!, assetTypology);
    }
  }, [quotes]);

  // const swiperControllerApartmentCarousel = useSwiperController({
  //   loop: true,
  //   pagination: true,
  // });

  const swiperController = useSwiperController();

  const isRoom = assetTypology === 'room';

  const roomBathroom = apartment?.features.privateBathroom
    ? {
        privateBathroom: true,
      }
    : {
        sharedBathroom: true,
      };

  const formatFeaturesSurface = (features?: CardApartmentProps['features']): CardApartmentProps['features'] => {
    if (!features) return {};
    return {
      ...features,
      surface: truncateTo1Decimal(features.surface),
    };
  };

  const apartmentFeatures =
    !isRoom && apartment
      ? {
          name: apartment.name,
          typologyName: apartment.typologyName,
          building: apartment.building,
          features: formatFeaturesSurface(apartment.features),
        }
      : {
          features: formatFeaturesSurface(apartment?.apartmentData?.features),
        };

  if (loading) return <PageLoader />;

  return (
    <div className='details-page'>
      <Layout>
        {apartment && (
          <>
            <Card borderLess className='card-details'>
              <div className='card-details-header'>
                <Link className='go-back-button' to={paths.root}>
                  <Icon iconName='chevron-left' />
                  <span>{t('details-page.go-back')}</span>
                </Link>
                <CardApartmentCarousel
                  onClick={(e) => toggleImageContainerModal(e, apartment.images)}
                  images={apartment.images}
                  swiperController={swiperController}
                  {...carouselProps}
                />
              </div>
              <div className='content'>
                {isRoom && (
                  <>
                    <CardApartmentInfo
                      className='details-page-info'
                      name={apartment.name}
                      typologyName={apartment.typologyName}
                      building={apartment.building}
                      title={t('details-page.room-features')}
                      features={{
                        ...roomBathroom,
                        surface: truncateTo1Decimal(apartment.features.surface),
                      }}
                      isQuoteSelected={false}
                    />
                    <h3 className='details-page-plan-title'>{t('details-page.plan')}</h3>
                    <div className='planimetry-container'>
                      <Button
                        onClick={(e) => toggleImageContainerModal(e, planImage.room)}
                        fluid
                        color='transparent'
                        className='planimetry'
                      >
                        <LazyImage.Image src={planImage.room} />
                        <Button
                          iconName='zoom-in'
                          onClick={(e) => toggleImageContainerModal(e, planImage.room)}
                          outlined
                          size='small'
                          iconSize='small'
                        />
                      </Button>
                    </div>
                    <Divider />
                  </>
                )}
                <CardApartmentInfo
                  className='details-page-info'
                  title={t('details-page.apartment-features')}
                  {...apartmentFeatures}
                  isQuoteSelected={false}
                  onClickSeeAllFeatures={showDownloadFeaturesButton ? onClickSeeAllFeatures : undefined}
                />
                {!isRoom && (
                  <>
                    <h3 className='details-page-plan-title'>{t('details-page.plan')}</h3>
                    <div className='planimetry-container'>
                      <Button
                        onClick={(e) => toggleImageContainerModal(e, planImage.apartment)}
                        fluid
                        color='transparent'
                        className='planimetry'
                      >
                        <LazyImage.Image src={planImage.apartment} />
                        <Button
                          iconName='zoom-in'
                          onClick={(e) => toggleImageContainerModal(e, planImage.apartment)}
                          outlined
                          size='small'
                          iconSize='small'
                        />
                      </Button>
                    </div>
                  </>
                )}
                <Divider />
                <CardQuoteContainer
                  quotes={hasSelectedQuote ? [apartment.quotes[0]] : apartment.quotes}
                  label={t('details-page.configurations')}
                  showSidebar={!hasSelectedQuote && device.type === 'desktop'}
                  apartmentName={`${t(`apartment-model.${apartment.typologyName}`, { defaultValue: apartment.typologyName })} ${apartment.name}`}
                  onQuoteDeleted={handleOnQuoteDeleted}
                />
                {hasSelectedQuote && apartment.quotes?.length > 1 && (
                  <Button
                    className='toggle-previous-quotes-button'
                    onClick={openPreviousQuotesModal}
                    iconName='clock-counter-clockwise'
                    color='transparent'
                    fluid
                  >
                    {t('details-page.configuration-history')}
                  </Button>
                )}
                <Divider />
                <FooterSection showContactUsModal={toggleContactUsModal} forceContactUsDisplay />
              </div>
              {device.type !== 'desktop' ? (
                <div className='banner-mobile'>
                  <Button onClick={bannerProps[apartment.status]?.firstButton?.onClick ?? toggleContactUsModal} fluid>
                    {bannerProps[apartment.status]?.firstButton?.label ?? t('card-apartment.highlighted-content.button-label.contact-us')}
                  </Button>
                </div>
              ) : (
                bannerProps[apartment.status] && <HighlightedContent {...bannerProps[apartment.status]} secondButton={undefined} />
              )}
            </Card>
            <ModalImageContainer
              images={modalImages}
              isOpen={openImageContainerModal}
              onClose={() => toggleImageContainerModal()}
              swiperController={swiperController}
              {...carouselProps}
            />
            <ContactUsModal isOpen={isContactUsModalOpen} toggleModal={toggleContactUsModal} showBanner={apartment.status === 'init'} />

            <QuoteDetailsModal
              isOpen={isQuoteDetailsModalOpen}
              onClose={handleOnClose}
              quoteList={quoteList}
              getModalTitle={getModalTitle}
              getModalDescription={getModalDescription}
              selectedQuote={selectedQuote}
              isSendRequestFlow={isSendRequestFlow}
              footerActionOnClose={isSendRequestFlow ? () => handleOnClose() : undefined}
              footerActionOnSubmit={isSendRequestFlow ? () => setIsSendRequestModalOpen(true) : undefined}
              onQuoteDeleted={handleOnQuoteDeleted}
            />
            {selectedQuote && (
              <SendRequestModal
                isOpen={isSendRequestModalOpen}
                onClose={() => setIsSendRequestModalOpen(false)}
                quote={selectedQuote}
                onSubmit={handleSendRequest}
              />
            )}
          </>
        )}
      </Layout>
    </div>
  );
};

export default React.memo(DetailsPage);
